import { Badge, Button } from "@light-technology/components";
import {
  DeviceType,
  type AppAccount,
  type AppResponse,
  type Device,
} from "../../state/api.types";
import { getManufacturerDisplayName } from "../shared/manufacturers";
import { formatDistanceToNowStrict } from "date-fns";
import { Check, Minus, RefreshCcw02 } from "@untitledui/icons";
import { api } from "../../state/api";
import { useState } from "react";
import { getNeedsAttention } from "../../utilities/devices";
import { useMutationWithToast } from "../../utilities/useMutationWithToast";

export const ComplianceChecklist = ({
  app,
  account,
  device,
}: {
  app: AppResponse;
  account: AppAccount;
  device: Device;
}) => {
  const [syncCooldown, setSyncCooldown] = useState(false);

  const complianceItems = device.program?.compliance_checklist;
  const isCompliant = device.program?.is_compliant;
  const [triggerSync, syncResult] = useMutationWithToast(
    api.useSyncDeviceMutation(),
    {
      errorMessage: "Failed to sync device. Please try again later.",
      onSuccess: () => {
        setSyncCooldown(true);
        setTimeout(() => {
          setSyncCooldown(false);
        }, 5000);
      },
    },
  );

  const manufacturerName = device.battery_details?.manufacturer
    ? getManufacturerDisplayName(device.battery_details.manufacturer)
    : null;

  // The badge reads the device's status, which every device type reports; the
  // checklist below stays on compliance, which only batteries populate.
  const [status, statusColor] = getNeedsAttention(device)
    ? (["Needs attention", "warning"] as const)
    : (["Connected", "success"] as const);

  return (
    <div className="flex flex-col gap-xl">
      <div>
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <div className="mb-xs text-sm font-medium text-secondary">
              State
            </div>
            <Badge color={statusColor} type="pill-color" size="sm">
              {status}
            </Badge>
          </div>
          <div className="flex flex-row items-center">
            <div className="text-xs text-secondary">
              {device.battery_details?.last_synced_at &&
                formatDistanceToNowStrict(
                  device.battery_details.last_synced_at,
                  { addSuffix: true },
                )}
            </div>
            {!syncCooldown ? (
              <Button
                size="sm"
                color="tertiary"
                iconLeading={RefreshCcw02}
                isLoading={syncResult.isLoading}
                onClick={() =>
                  triggerSync({
                    appUUID: app.uuid,
                    accountUUID: account.uuid,
                    deviceUUID: device.uuid,
                  })
                }
              />
            ) : (
              <Button size="sm" color="tertiary" iconLeading={Check} disabled />
            )}
          </div>
        </div>
        {!isCompliant && device.device_type === DeviceType.Battery ? (
          <div className="mt-xs text-xs text-tertiary">
            {account.first_name || "The user"} or their installer needs to apply
            the following settings in the {manufacturerName ?? "manufacturer's"}{" "}
            app.
          </div>
        ) : null}
      </div>
      {device.device_type === DeviceType.Battery ? (
        <div className="flex flex-col gap-lg">
          {complianceItems?.length &&
          complianceItems.find((item) => item.actual !== null) ? (
            complianceItems.map((complianceItem) => {
              return (
                <div
                  key={complianceItem.field}
                  className="flex items-center gap-md"
                >
                  {complianceItem.actual === complianceItem.expected ? (
                    <Check className="size-xl" />
                  ) : (
                    <Minus className="size-xl text-fg-quaternary" />
                  )}
                  <div className="text-xs">{complianceItem.label}</div>
                </div>
              );
            })
          ) : (
            <div className="rounded-xl bg-bg-tertiary p-xl text-xs">
              Needed permissions are still being collected from{" "}
              {manufacturerName}. Please check back later.
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};
